import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { translate } from '@fiverr-private/i18n-react';
import { Radio } from '@fiverr-private/fit';
import { FILTER, PriceConverter } from '@fiverr-private/listing_lib';
import { Input, InputGroup, InputPrefix } from '@fiverr-private/forms';
import { Stack } from '@fiverr-private/layout_components';
import { onRangeChange, selectCustomOnFocus } from './PriceRange.utils';
import {
    CUSTOM_BUDGET_VALUE,
    DOMAttributes,
    MAX_GIG_PRICE_RANGE,
    MIN_GIG_PRICE,
    MIN_GIG_PRICE_RANGE,
    RANGE_KEYS,
} from './PriceRange.constants';

interface PriceRangeProps {
    currencySymbol: string;
    currency: { code: string };
    budgetValue: { min: number; max: number };
    setBudgetValue: (value: { min: number; max: number }) => void;
    isCustomBucketSelected: boolean;
    hasPriceBuckets: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setIsValidFilter: (value: boolean) => void;
}

export const PriceRange: React.FC<PriceRangeProps> = ({
    currencySymbol,
    currency,
    budgetValue,
    setBudgetValue,
    isCustomBucketSelected,
    hasPriceBuckets,
    onChange,
    setIsValidFilter,
}) => {
    const [isMaxValueValid, setIsMaxValueValid] = useState(true);
    const customRadioRef = useRef<any>(null);
    const { max } = budgetValue || {};
    const priceConverter = new PriceConverter(currency);
    const minGigPrice = Math.ceil(priceConverter.convert(MIN_GIG_PRICE));
    const minGigPriceTemplate = `${currencySymbol + minGigPrice}`;

    const customPriceLabel = translate('search_perseus.filters.global.price_buckets.custom');
    const validationAlertText = translate('search_perseus.filter_builder.selected.price_range.services_start', {
        params: { price: minGigPriceTemplate },
    });

    useEffect(() => {
        if (!isCustomBucketSelected) {
            setIsMaxValueValid(true);
        }
    }, [isCustomBucketSelected]);

    const validateMaxPriceValue = ({ target: { value } }) => {
        const checkMaxValue = !value || minGigPrice <= value;

        setIsMaxValueValid(checkMaxValue);
        setIsValidFilter(checkMaxValue);
    };

    const handleMaxChange = ({ target }) => {
        onRangeChange(RANGE_KEYS.MAX, target.value, budgetValue, setBudgetValue);

        if (!isMaxValueValid) {
            validateMaxPriceValue({ target });
        }
    };

    return (
        <div
            className={classnames('price-range-filter', {
                'radio-list': hasPriceBuckets,
            })}
        >
            {hasPriceBuckets && (
                <div className="radio-item-wrapper">
                    <Radio
                        ref={customRadioRef}
                        value={CUSTOM_BUDGET_VALUE}
                        name={CUSTOM_BUDGET_VALUE}
                        checked={isCustomBucketSelected}
                        className={classnames('price-bucket-button m-b-12 radio-item', {
                            selected: isCustomBucketSelected,
                        })}
                        onChange={onChange}
                    >
                        <div className="inner-radio">{customPriceLabel}</div>
                    </Radio>
                </div>
            )}
            <div className="price-range-filter-inputs p-b-16" onClick={() => customRadioRef.current?.click()}>
                <div className="input-wrapper">
                    <label>{translate('search_perseus.filter_builder.selected.price_range.up_to')}</label>
                    <Stack as="div" direction="row" alignItems="center" gap="2">
                        <InputGroup>
                            <InputPrefix>{currencySymbol}</InputPrefix>
                            <Input
                                className={classnames('max', {
                                    'danger-border': !isMaxValueValid,
                                })}
                                type="number"
                                id={`${FILTER.GIG_PRICE_RANGE.ID}_${RANGE_KEYS.MAX}`}
                                name={FILTER.GIG_PRICE_RANGE.ID}
                                onFocus={() => selectCustomOnFocus(budgetValue, setBudgetValue)}
                                min={MIN_GIG_PRICE_RANGE}
                                max={MAX_GIG_PRICE_RANGE}
                                value={max && max !== MAX_GIG_PRICE_RANGE ? max : ''}
                                onBlur={validateMaxPriceValue}
                                onChange={handleMaxChange}
                                {...DOMAttributes}
                            />
                        </InputGroup>
                    </Stack>
                </div>
            </div>
            {!isMaxValueValid && <p className="validation-alert tbody-6">{validationAlertText}</p>}
        </div>
    );
};
